/**
 * Pure data-conversion helpers: REST-JSON role menus  ⇄  RoleMenuT.
 *
 * The permission catalogue these menus are drawn from is LiftCloud's, fetched
 * from GET /api/permission-catalogue/; helpers that need it take it as a
 * parameter rather than importing a bundled copy.
 *
 * No React, no Redux, no Ant Design — import freely from any layer.
 */

import { MenuActionT } from "../../../flatbuffers/generated/archive/menu-action";
import { MenuPageT } from "../../../flatbuffers/generated/archive/menu-page";
import { RoleMenuT } from "../../../flatbuffers/generated/archive/role-menu";
import { RolePermissionGrantT } from "../../../flatbuffers/generated/archive/role-permission-grant";

// ---------------------------------------------------------------------------
// Text normalisation helper
// ---------------------------------------------------------------------------

/** Normalise a FlatBuffer string field (string | Uint8Array | null) to plain string. */
export const text = (v: string | Uint8Array | null): string => (typeof v === "string" ? v : "");

// ---------------------------------------------------------------------------
// Public interfaces
// ---------------------------------------------------------------------------

export interface MenuActionJson {
  name: string;
  active: boolean;
  offline: boolean;
  tree_key: string;
  uri: string;
  /** True once an operator can no longer grant/revoke this action — its switches stay disabled. */
  locked?: boolean;
}

export interface MenuPageJson {
  name: string;
  active: boolean;
  offline: boolean;
  tree_key: string;
  /** Absent (not just []) on a page/sub_page that has no actions of its own — every reader falls back with `?? []`. */
  actions?: MenuActionJson[];
  /** Absent (not just []) on a page/sub_page with no children — every reader falls back with `?? []`. */
  sub_pages?: MenuPageJson[];
  /** True once an operator can no longer grant/revoke anything under this page — its switches stay disabled. */
  locked?: boolean;
}

export interface RoleMenuJson {
  name: string;
  active: boolean;
  offline: boolean;
  pages: MenuPageJson[];
  /** The role menu's OWN description (RoleMenuT.description) — distinct from
   * the per-page/action descriptions collectMenuDescriptions reads. */
  description?: string;
  /** The role's explicit permission decisions, by catalogue key (see PermissionGrantsJson). */
  permission_grants?: PermissionGrantsJson;
}

/**
 * One explicit permission decision, as LiftCloud stores it
 * (permissions.Grant). This is the JSON shape; on the FlatBuffer wire the same
 * decision is an Archive.RolePermissionGrant (see permissionGrantsToVector).
 */
export interface PermissionGrantJson {
  active: boolean;
  offline: boolean;
  locked?: boolean;
}

/**
 * A role's explicit decisions keyed by catalogue key. A key that is absent is
 * UNDECIDED, which is not the same as denied: LiftCloud's rules decide it (e.g.
 * view-monitoring-dashboard is granted to every role that has not decided it).
 */
export type PermissionGrantsJson = Record<string, PermissionGrantJson>;

export type PermissionMode = "online" | "offline";

export interface PermissionTreeNode {
  title: string;
  key: string;
  children?: PermissionTreeNode[];
}

// ---------------------------------------------------------------------------
// menuJsonToRoleMenuT
// ---------------------------------------------------------------------------

// Input here may be the REST wire shape (snake_case: tree_key, sub_pages) OR
// a FlatBuffer-unpacked object (camelCase: treeKey, subPages — the Object
// API's own field names, e.g. straight off userLogged.role.menu). Reading
// both is NOT optional: silently accepting only one shape means the other
// parses "successfully" into a menu with every sub_pages array empty, which
// is indistinguishable from "this role really has no sub-pages" until you
// diff against the source object. Same dual-alias pattern normalizeUser.ts
// already uses for this same REST-vs-FlatBuffer ambiguity.
function actionJsonToT(a: any): MenuActionT {
  return new MenuActionT(
    a.name,
    a.active,
    a.tree_key ?? a.treeKey,
    a.uri,
    a.offline ?? true,
    null,
    a.locked ?? false,
  );
}

function pageJsonToT(p: any): MenuPageT {
  return new MenuPageT(
    p.name,
    p.active,
    p.offline ?? true,
    p.tree_key ?? p.treeKey,
    (p.actions ?? []).map(actionJsonToT),
    (p.sub_pages ?? p.subPages ?? []).map(pageJsonToT),
    null,
    p.locked ?? false,
  );
}

/** Convert a REST/JSON menu object into a fully-typed RoleMenuT instance tree. */
export function menuJsonToRoleMenuT(json: RoleMenuJson): RoleMenuT {
  return new RoleMenuT(
    json.name,
    json.active,
    json.offline ?? true,
    (json.pages ?? []).map(pageJsonToT),
  );
}

// ---------------------------------------------------------------------------
// roleMenuTToMenuJson
// ---------------------------------------------------------------------------

function actionTToJson(a: MenuActionT): MenuActionJson {
  return {
    name: text(a.name),
    active: a.active,
    offline: a.offline,
    tree_key: text(a.treeKey),
    uri: text(a.uri),
    locked: a.locked,
  };
}

function pageTToJson(p: MenuPageT): MenuPageJson {
  return {
    name: text(p.name),
    active: p.active,
    offline: p.offline,
    tree_key: text(p.treeKey),
    actions: (p.actions ?? []).map(actionTToJson),
    sub_pages: (p.subPages ?? []).map(pageTToJson),
    locked: p.locked,
  };
}

/**
 * Serialise a RoleMenuT back to the snake_case JSON shape accepted by the
 * LiftCloud REST API.  Every page/sub_page always emits `actions` and
 * `sub_pages` arrays (empty when absent) for deterministic validator behaviour.
 */
export function roleMenuTToMenuJson(menu: RoleMenuT): RoleMenuJson {
  return {
    name: text(menu.name),
    active: menu.active,
    offline: menu.offline,
    pages: (menu.pages ?? []).map(pageTToJson),
  };
}

// ---------------------------------------------------------------------------
// duplicateRoleMenuT  (deep clone via constructors, not JSON round-trip)
// ---------------------------------------------------------------------------

function cloneAction(a: MenuActionT): MenuActionT {
  return new MenuActionT(a.name, a.active, a.treeKey, a.uri, a.offline, a.description, a.locked);
}

function clonePage(p: MenuPageT): MenuPageT {
  return new MenuPageT(
    p.name,
    p.active,
    p.offline,
    p.treeKey,
    (p.actions ?? []).map(cloneAction),
    (p.subPages ?? []).map(clonePage),
    p.description,
    p.locked,
  );
}

/**
 * Deep clone a RoleMenuT, rebuilding all class instances from their
 * constructors so that no reference is shared between original and copy.
 */
export function duplicateRoleMenuT(menu: RoleMenuT): RoleMenuT {
  return new RoleMenuT(menu.name, menu.active, menu.offline, (menu.pages ?? []).map(clonePage));
}

// ---------------------------------------------------------------------------
// collectActionTreeKeys
// ---------------------------------------------------------------------------

function collectFromPage(page: MenuPageT, seen: Set<string>, out: string[]): void {
  (page.actions ?? []).forEach((a) => {
    const k = text(a.treeKey);
    if (k && !seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  });
  (page.subPages ?? []).forEach((sp) => collectFromPage(sp, seen, out));
}

/**
 * Return every distinct, non-empty action tree_key in depth-first order.
 */
export function collectActionTreeKeys(menu: RoleMenuT): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  (menu.pages ?? []).forEach((page) => collectFromPage(page, seen, out));
  return out;
}

// ---------------------------------------------------------------------------
// collectLockedActionTreeKeys
// ---------------------------------------------------------------------------

// A locked page/sub_page locks every action beneath it, recursively, even
// one that isn't itself marked locked — same cascade collectPageActionKeys
// applies for the bulk-toggle guard, kept in sync with it here.
function collectLockedFromPage(page: MenuPageT, inheritedLocked: boolean, out: string[]): void {
  const pageLocked = inheritedLocked || !!page.locked;
  (page.actions ?? []).forEach((a) => {
    if (pageLocked || a.locked) {
      const k = text(a.treeKey);
      if (k) out.push(k);
    }
  });
  (page.subPages ?? []).forEach((sp) => collectLockedFromPage(sp, pageLocked, out));
}

/**
 * Return every distinct action tree_key that is locked — either directly
 * (`action.locked`) or by inheriting a locked ancestor page/sub_page.
 *
 * Used when seeding a fresh, all-unchecked menu (create role / role-base
 * fallback): a locked permission must start GRANTED rather than blank, since
 * the operator can never uncheck its switch anyway.
 */
export function collectLockedActionTreeKeys(menu: RoleMenuT): string[] {
  const out: string[] = [];
  (menu.pages ?? []).forEach((page) => collectLockedFromPage(page, false, out));
  return out;
}

// ---------------------------------------------------------------------------
// roleMenuTreeData
// ---------------------------------------------------------------------------

function modeFlag(a: MenuActionT, mode: PermissionMode): boolean {
  return mode === "online" ? a.active : a.offline;
}

/**
 * Build a human-readable, per-page-unique label for an action leaf.
 *
 * Every action's `name` is only the CRUD verb ("view"/"create"/...), so a page
 * with several endpoints (e.g. servers: view, view-light, export, import,
 * oauth-create) would render as a wall of identical "view"/"create" rows. The
 * `tree_key` is the real per-endpoint identifier, so we label from its
 * page-scoped suffix ("view light", "export", "oauth create") — falling back to
 * the verb when the key is absent or unprefixed.
 */
export function actionTitle(action: MenuActionT, pageKey: string): string {
  const name = text(action.name);
  const key = text(action.treeKey);
  if (!key) return name;
  const suffix = pageKey && key.startsWith(`${pageKey}-`) ? key.slice(pageKey.length + 1) : key;
  const label = suffix.replace(/-/g, " ").trim();
  return label || name;
}

/** Display form of a permission name: hyphens become spaces and each word starts in capitals ("view-global" -> "View Global"). */
export function formatPermissionLabel(label: string): string {
  return label
    .split(/-+|\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function buildPageNode(
  page: MenuPageT,
  mode: PermissionMode,
  pageIndex: number,
  keyPath: string,
  checkedKeys: string[],
): PermissionTreeNode {
  const pageKey = text(page.treeKey) || `${keyPath}/${pageIndex}`;

  const subPageNodes: PermissionTreeNode[] = (page.subPages ?? []).map((sp, i) =>
    buildPageNode(sp, mode, i, pageKey, checkedKeys),
  );

  const actionNodes: PermissionTreeNode[] = (page.actions ?? []).map((a) => {
    const actionKey = text(a.treeKey);
    if (modeFlag(a, mode)) {
      checkedKeys.push(actionKey);
    }
    return { title: actionTitle(a, pageKey), key: actionKey };
  });

  const children: PermissionTreeNode[] = [...subPageNodes, ...actionNodes];

  const node: PermissionTreeNode = {
    title: text(page.name),
    key: pageKey,
  };
  if (children.length > 0) {
    node.children = children;
  }
  return node;
}

/**
 * Build the AntD TreeSelect data alongside the checkedKeys for the given mode.
 *
 * Page/sub_page nodes are NOT included in checkedKeys — AntD's checkStrictly=false
 * derives parent state from its children automatically.
 */
export function roleMenuTreeData(
  menu: RoleMenuT,
  mode: PermissionMode,
): { treeData: PermissionTreeNode[]; checkedKeys: string[] } {
  const checkedKeys: string[] = [];
  const treeData: PermissionTreeNode[] = (menu.pages ?? []).map((page, i) =>
    buildPageNode(page, mode, i, "page", checkedKeys),
  );
  return { treeData, checkedKeys };
}

// ---------------------------------------------------------------------------
// applyCheckedKeys
// ---------------------------------------------------------------------------

function applyToPage(
  page: MenuPageT,
  checkedKeys: ReadonlySet<string>,
  mode: PermissionMode,
): MenuPageT {
  const updatedActions = (page.actions ?? []).map((a) => {
    const flag = checkedKeys.has(text(a.treeKey));
    if (mode === "online") {
      return new MenuActionT(a.name, flag, a.treeKey, a.uri, a.offline, a.description, a.locked);
    }
    return new MenuActionT(a.name, a.active, a.treeKey, a.uri, flag, a.description, a.locked);
  });

  const updatedSubPages = (page.subPages ?? []).map((sp) => applyToPage(sp, checkedKeys, mode));

  // This page's mode-flag derives from its fresh children directly — the
  // sub-pages' flags were just computed by the recursive calls, so a second
  // full-tree traversal would be wasted work.
  const enabled =
    updatedActions.some((a) => modeFlag(a, mode)) ||
    updatedSubPages.some((sp) => (mode === "online" ? sp.active : sp.offline));

  return new MenuPageT(
    page.name,
    mode === "online" ? enabled : page.active,
    mode === "offline" ? enabled : page.offline,
    page.treeKey,
    updatedActions,
    updatedSubPages,
    page.description,
    page.locked,
  );
}

/**
 * Pure function: apply a new set of checked action keys to a RoleMenuT for
 * one PermissionMode, returning a fresh tree without mutating input.
 *
 * - Only actions whose tree_key is in `checkedKeys` have their mode-flag set.
 * - Each page/sub_page mode-flag is derived from whether any descendant action
 *   has its mode-flag set (true = at least one, false = none).
 * - The OTHER mode's flags on every action, page, and sub_page are untouched.
 * - Top-level RoleMenuT name/active/offline are untouched.
 *
 * Runs on every checkbox click over a ~270-action tree, so lookups use a
 * Set (O(1) vs O(n) per action) and the rebuild allocates each node exactly
 * once — applyToPage constructs every page/action fresh, making a separate
 * pre-clone of the tree pure waste.
 */
export function applyCheckedKeys(
  menu: RoleMenuT,
  checkedKeys: ReadonlyArray<string>,
  mode: PermissionMode,
): RoleMenuT {
  const checkedSet: ReadonlySet<string> = new Set(checkedKeys);
  return new RoleMenuT(
    menu.name,
    menu.active,
    menu.offline,
    (menu.pages ?? []).map((page) => applyToPage(page, checkedSet, mode)),
  );
}

// ---------------------------------------------------------------------------
// resolveTreeKeyToActionKeys
// ---------------------------------------------------------------------------

// Locked actions/pages are skipped entirely — a bulk toggle over a
// page/sub_page must not reach past an individually locked action, nor into
// a sub_page that is itself locked (locked cascades to everything under it,
// same as granted state cascades up from its descendants).
function collectPageActionKeys(page: MenuPageT, out: string[]): void {
  (page.actions ?? []).forEach((a) => {
    if (a.locked) return;
    const k = text(a.treeKey);
    if (k) out.push(k);
  });
  (page.subPages ?? []).forEach((sp) => {
    if (sp.locked) return;
    collectPageActionKeys(sp, out);
  });
}

function findNode(
  pages: MenuPageT[],
  treeKey: string,
): { kind: "page"; page: MenuPageT } | { kind: "action" } | null {
  for (let i = 0; i < pages.length; i += 1) {
    const page = pages[i];
    if (text(page.treeKey) === treeKey) return { kind: "page", page };
    if ((page.actions ?? []).some((a) => text(a.treeKey) === treeKey)) return { kind: "action" };
    const inSubPages = findNode(page.subPages ?? [], treeKey);
    if (inSubPages) return inSubPages;
  }
  return null;
}

/**
 * Resolve a card/section's `tree_key` (page, sub_page, or leaf action) to the
 * set of leaf action tree_keys it controls.
 *
 * - An action key resolves to itself (a single-element array) — toggling a
 *   leaf switch only ever affects that one action.
 * - A page/sub_page key resolves to every action tree_key nested under it
 *   (recursively, across all of its sub_pages) — toggling a card/section-level
 *   switch is a bulk toggle over its whole subtree, mirroring the cascade
 *   AntD's checkable Tree already applied when a parent node was (un)checked.
 *   Individually locked actions, and any locked sub_page's whole subtree, are
 *   excluded from that bulk set — a card-level toggle must not reach past a
 *   lock its own per-row switch would refuse to cross.
 *
 * Returns [] if treeKey is not found (should not happen for a key that came
 * from rendering this same menu).
 */
export function resolveTreeKeyToActionKeys(menu: RoleMenuT, treeKey: string): string[] {
  const found = findNode(menu.pages ?? [], treeKey);
  if (!found) return [];
  if (found.kind === "action") return [treeKey];
  const out: string[] = [];
  collectPageActionKeys(found.page, out);
  return out;
}

// ---------------------------------------------------------------------------
// toggleTreeKeysOneMode
// ---------------------------------------------------------------------------

/**
 * Bulk-toggle a set of card/section-level tree_keys in a SINGLE
 * PermissionMode, returning the resulting menu. See
 * `toggleTreeKeysBothModes` for the same operation across both modes at
 * once — this is the single-mode counterpart, extracted from
 * RolesPermissionsCards' `toggleMany` so every card/section-level toggle in
 * the app (the split Online/Offline columns there, and the Sidebar block's
 * own switches) shares one implementation.
 */
export function toggleTreeKeysOneMode(
  menu: RoleMenuT,
  treeKeys: ReadonlyArray<string>,
  mode: PermissionMode,
  nextValue: boolean,
): RoleMenuT {
  const checked = new Set(roleMenuTreeData(menu, mode).checkedKeys);
  treeKeys.forEach((treeKey) => {
    resolveTreeKeyToActionKeys(menu, treeKey).forEach((actionKey) => {
      if (nextValue) checked.add(actionKey);
      else checked.delete(actionKey);
    });
  });
  return applyCheckedKeys(menu, Array.from(checked), mode);
}

// ---------------------------------------------------------------------------
// getMenuNodeFlags
// ---------------------------------------------------------------------------

function findNodeFlags(
  pages: MenuPageT[],
  treeKey: string,
): { active: boolean; offline: boolean; locked: boolean } | null {
  for (let i = 0; i < pages.length; i += 1) {
    const page = pages[i];
    if (text(page.treeKey) === treeKey) {
      return { active: page.active, offline: page.offline, locked: !!page.locked };
    }
    const action = (page.actions ?? []).find((a) => text(a.treeKey) === treeKey);
    if (action) {
      return { active: action.active, offline: action.offline, locked: !!action.locked };
    }
    const nested = findNodeFlags(page.subPages ?? [], treeKey);
    if (nested) return nested;
  }
  return null;
}

/**
 * Read a page/sub_page/action's current active/offline/locked flags by its
 * tree_key — the same lookup `resolveTreeKeyToActionKeys` already does to
 * find the node, exposed here for a caller that only needs to know its
 * CURRENT state (e.g. a switch's `checked` prop) rather than the leaf
 * action keys under it. Returns null when the tree_key is not present in
 * this menu, which a caller reads as "unknown / not granted" rather than
 * treating as an error — a role's menu is a snapshot that may simply
 * predate a given tree_key (see useEffectivePermission's comment on the
 * same staleness class).
 */
export function getMenuNodeFlags(
  menu: RoleMenuT,
  treeKey: string,
): { active: boolean; offline: boolean; locked: boolean } | null {
  return findNodeFlags(menu.pages ?? [], treeKey);
}

// ---------------------------------------------------------------------------
// toggleTreeKeysBothModes
// ---------------------------------------------------------------------------

/**
 * Bulk-toggle a set of card/section-level tree_keys in BOTH online and
 * offline modes at once, returning the resulting menu.
 *
 * Each `treeKeys` entry is resolved via `resolveTreeKeyToActionKeys` first
 * (a page/sub_page key expands to every leaf action beneath it; an action
 * key resolves to itself), then both modes are unioned/removed in one pass
 * and applied with two chained `applyCheckedKeys` calls — the second built
 * on the first's result, not on the original `menu` again, so one caller
 * update carries both mode changes instead of the second silently
 * overwriting the first (see RolesPermissionsCards' `toggleManyBoth`, the
 * original of this logic, for why two independent `onChange` calls would
 * lose one mode's change).
 */
export function toggleTreeKeysBothModes(
  menu: RoleMenuT,
  treeKeys: ReadonlyArray<string>,
  nextValue: boolean,
): RoleMenuT {
  const onlineChecked = new Set(roleMenuTreeData(menu, "online").checkedKeys);
  const offlineChecked = new Set(roleMenuTreeData(menu, "offline").checkedKeys);
  treeKeys.forEach((treeKey) => {
    resolveTreeKeyToActionKeys(menu, treeKey).forEach((actionKey) => {
      if (nextValue) {
        onlineChecked.add(actionKey);
        offlineChecked.add(actionKey);
      } else {
        onlineChecked.delete(actionKey);
        offlineChecked.delete(actionKey);
      }
    });
  });
  const withOnline = applyCheckedKeys(menu, Array.from(onlineChecked), "online");
  return applyCheckedKeys(withOnline, Array.from(offlineChecked), "offline");
}

// ---------------------------------------------------------------------------
// pruneGrantedMenu
// ---------------------------------------------------------------------------

function pruneAction(a: MenuActionT): MenuActionT | null {
  if (!a.active && !a.offline) return null;
  return a;
}

function prunePage(page: MenuPageT): MenuPageT | null {
  const prunedActions = (page.actions ?? [])
    .map(pruneAction)
    .filter((a): a is MenuActionT => a !== null);
  const prunedSubPages = (page.subPages ?? [])
    .map(prunePage)
    .filter((p): p is MenuPageT => p !== null);

  if (prunedActions.length === 0 && prunedSubPages.length === 0) return null;

  return new MenuPageT(
    page.name,
    page.active,
    page.offline,
    page.treeKey,
    prunedActions,
    prunedSubPages,
    page.description,
    page.locked,
  );
}

/**
 * Drop every action/page/sub_page that is fully un-granted (both `active`
 * and `offline` false) before a menu is sent to the backend on role
 * creation. A page/sub_page that ends up with no actions and no sub_pages
 * left is dropped too — its own active/offline flags are always derived
 * from its descendants (see applyToPage), so "nothing survived under it"
 * and "its own flags are false" are the same condition.
 *
 * The live, editable menu kept in component state is NEVER pruned — only
 * the copy built right before roleMenuTToMenuJson for the POST body. The
 * operator must keep seeing the creator's full permission ceiling at all
 * times so they can grant anything they hold, not just what's currently on.
 */
export function pruneGrantedMenu(menu: RoleMenuT): RoleMenuT {
  const prunedPages = (menu.pages ?? []).map(prunePage).filter((p): p is MenuPageT => p !== null);
  return new RoleMenuT(menu.name, menu.active, menu.offline, prunedPages);
}

// ---------------------------------------------------------------------------
// Permission grants (explicit decisions)
// ---------------------------------------------------------------------------

function grantOf(node: MenuPageT | MenuActionT): PermissionGrantJson {
  return { active: node.active, offline: node.offline, ...(node.locked && { locked: true }) };
}

function grantsFromPage(page: MenuPageT): [string, PermissionGrantJson][] {
  const own: [string, PermissionGrantJson][] = text(page.treeKey)
    ? [[text(page.treeKey), grantOf(page)]]
    : [];
  const actions = (page.actions ?? [])
    .filter((a) => text(a.treeKey))
    .map((a): [string, PermissionGrantJson] => [text(a.treeKey), grantOf(a)]);
  return [...own, ...actions, ...(page.subPages ?? []).flatMap(grantsFromPage)];
}

/**
 * One explicit decision for EVERY page, sub_page and action in `menu`, taken
 * from its switches: a switched-off node becomes an explicit denial, which a
 * LiftCloud default rule can no longer override.
 */
export function menuToPermissionGrants(menu: RoleMenuT): PermissionGrantsJson {
  return Object.fromEntries((menu.pages ?? []).flatMap(grantsFromPage));
}

/**
 * The decisions a JSON `permission_grants` object holds (LiftCloud's
 * permissions.Grants). Anything that is not such an object reads as none.
 */
export function grantsObject(raw: unknown): PermissionGrantsJson {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: PermissionGrantsJson = {};
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    if (!value || typeof value !== "object") return;
    const g = value as Record<string, unknown>;
    out[key] = {
      active: g.active === true,
      offline: g.offline === true,
      ...(g.locked === true && { locked: true }),
    };
  });
  return out;
}

function menuRootGrants(rawMenu: unknown): PermissionGrantsJson {
  try {
    const obj = typeof rawMenu === "string" ? JSON.parse(rawMenu) : rawMenu;
    if (!obj || typeof obj !== "object") return {};
    const root = obj as Record<string, unknown>;
    return grantsObject(root.permission_grants ?? root.permissionGrants);
  } catch {
    return {};
  }
}

/**
 * `grants` as the Archive.Role `permission_grants` vector, in key order so one
 * role always packs to the same bytes.
 */
export function permissionGrantsToVector(grants: PermissionGrantsJson): RolePermissionGrantT[] {
  return Object.keys(grants)
    .sort()
    .map((key) => {
      const g = grants[key];
      return new RolePermissionGrantT(key, g.active, g.offline, g.locked === true);
    });
}

/**
 * The decisions an Archive.Role `permission_grants` vector holds. Entries are
 * read field by field rather than by class, because a RoleT that has been
 * through Redux (structuredClone) has lost its prototype. Entries without a
 * key decide nothing; a repeated key keeps its last entry. Anything that is
 * not a vector reads as no decisions.
 */
export function permissionGrantsFromVector(raw: unknown): PermissionGrantsJson {
  if (!Array.isArray(raw)) return {};
  const out: PermissionGrantsJson = {};
  raw.forEach((item: unknown) => {
    if (!item || typeof item !== "object") return;
    const g = item as Partial<RolePermissionGrantT>;
    const key = typeof g.key === "string" ? g.key : "";
    if (!key) return;
    out[key] = {
      active: g.active === true,
      offline: g.offline === true,
      ...(g.locked === true && { locked: true }),
    };
  });
  return out;
}

/**
 * A role's existing explicit decisions, read from a role record as LiftCloud
 * returns it.
 *
 * A FlatBuffer role (RoleT) from a LiftCloud that knows the field carries them
 * as the `permissionGrants` vector, and when that holds anything it is the
 * answer. Otherwise they are read the way older LiftClouds send them: the root
 * of the rendered menu (`menu` / `menuJson` / `menu_json`, JSON string or
 * object) carries `permission_grants`, and a JSON role also has them
 * top-level. Those sources are merged; the top-level copy wins.
 */
export function readRolePermissionGrants(role: unknown): PermissionGrantsJson {
  if (!role || typeof role !== "object") return {};
  const r = role as Record<string, unknown>;
  const structured = permissionGrantsFromVector(r.permissionGrants ?? r.permission_grants);
  if (Object.keys(structured).length > 0) return structured;
  return {
    ...menuRootGrants(r.menu_json),
    ...menuRootGrants(r.menuJson),
    ...menuRootGrants(r.menu),
    ...grantsObject(r.permission_grants ?? r.permissionGrants),
  };
}

/**
 * The menu to save for a role edited as `menu` — the FULL edited tree, never
 * the pruned display copy — with its explicit decisions embedded at the root.
 *
 * `permission_grants` is `existing` (the role's decisions before the edit)
 * overlaid with one decision per node of `menu`. That way a switch the
 * operator turned off is saved as an explicit denial, and a decision on a key
 * the form did not show (pruned by the editor's ceiling) is kept instead of
 * falling back to undecided.
 *
 * Role writes are FlatBuffer (Archive.Role). The request serializer sends
 * these grants twice: as the structured `permission_grants` vector, which a
 * LiftCloud that knows it reads first, and embedded here at the menu root,
 * which is all an older LiftCloud reads. Its ConvertLegacyMenu takes embedded
 * grants as-is whenever they resolve to exactly the flags the tree shows,
 * which a decision on every shown node guarantees.
 */
export function roleMenuSaveJson(
  menu: RoleMenuT,
  existing: PermissionGrantsJson = {},
): RoleMenuJson {
  const json = roleMenuTToMenuJson(menu);
  json.permission_grants = { ...existing, ...menuToPermissionGrants(menu) };
  return json;
}

// ---------------------------------------------------------------------------
// editorCeilingMenu
// ---------------------------------------------------------------------------

/**
 * The tree a roles editor may grant from: what the EDITING user's own role
 * holds.
 *
 * LiftCloud renders every role's `menu` as the whole permission catalogue with
 * that role's effective flags, so un-held permissions arrive as nodes with both
 * flags off. They are pruned here, which keeps "you can only grant what you
 * hold" true in the editor: the server refuses a role anything beyond its
 * editor's own permissions (403), so offering those switches would only set up
 * a failed save.
 *
 * Falls back to the catalogue itself (every flag on) when the user's menu is
 * absent or unparseable, and to null while that catalogue has not loaded.
 */
export function editorCeilingMenu(
  rawUserMenu: unknown,
  catalogueMenu: RoleMenuJson | null | undefined,
): RoleMenuT | null {
  const own = safeParseRoleMenu(rawUserMenu);
  if (own) return pruneGrantedMenu(own);
  return catalogueMenu ? menuJsonToRoleMenuT(catalogueMenu) : null;
}

// ---------------------------------------------------------------------------
// collectMenuDescriptions
// ---------------------------------------------------------------------------

function collectDescriptionsFromNode(node: any, out: Map<string, string>): void {
  if (!node || typeof node !== "object") return;
  const treeKey = node.tree_key ?? node.treeKey;
  const description = node.description;
  if (typeof treeKey === "string" && treeKey && typeof description === "string") {
    const trimmed = description.trim();
    // First writer wins: a menu that carries a description for a key is
    // preferred over a later source that repeats the key with an empty one.
    if (trimmed && !out.has(treeKey)) out.set(treeKey, trimmed);
  }
  (node.actions ?? []).forEach((a: any) => collectDescriptionsFromNode(a, out));
  (node.sub_pages ?? node.subPages ?? []).forEach((sp: any) =>
    collectDescriptionsFromNode(sp, out),
  );
}

/**
 * Build a `tree_key -> description` lookup from a RAW role menu (JSON string
 * or already-parsed object, snake_case or camelCase).
 *
 * `description` is a backend-side annotation on each page/sub_page/action
 * ("View or hiding global tours."). It is NOT part of the MenuPage/MenuAction
 * FlatBuffer schema, so menuJsonToRoleMenuT necessarily drops it — a RoleMenuT
 * simply has nowhere to put it, and inventing a local look-alike type to carry
 * it would be exactly the mirrored-struct the FlatBuffer rule forbids. This
 * keeps it as a separate, purely presentational side-channel keyed by the same
 * tree_key the menu tree already uses, so the editor can show it without the
 * grant model pretending to own a field the wire format doesn't have. Once the
 * schema does gain `description`, this becomes unnecessary and callers can read
 * it straight off the node.
 *
 * Accepts several raw menus so a caller can merge sources (e.g. the editing
 * user's ceiling menu plus the edited role's own menu) — descriptions are
 * global per tree_key, only their presence differs per source.
 */
export function collectMenuDescriptions(...raws: unknown[]): Record<string, string> {
  const found = new Map<string, string>();
  raws.forEach((raw) => {
    try {
      const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (!obj || typeof obj !== "object") return;
      ((obj as any).pages ?? []).forEach((page: any) => collectDescriptionsFromNode(page, found));
    } catch {
      // A menu that does not parse contributes no descriptions — the tooltip
      // falls back to its "description not added" placeholder.
    }
  });
  return Object.fromEntries(found);
}

// ---------------------------------------------------------------------------
// readMenuDescription
// ---------------------------------------------------------------------------

/**
 * Read the top-level `description` off a RAW role menu (JSON string or
 * already-parsed object) — the menu's OWN description
 * (e.g. `{ name: "ORIGINMENU", pages: [...], description: "..." }`), NOT the
 * per-page/action descriptions collectMenuDescriptions builds a side-map for.
 * Returns "" when absent or the raw menu doesn't parse.
 */
export function readMenuDescription(raw: unknown): string {
  try {
    const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
    const description = (obj as { description?: unknown } | null)?.description;
    return typeof description === "string" ? description : "";
  } catch {
    return "";
  }
}

// ---------------------------------------------------------------------------
// safeParseRoleMenu
// ---------------------------------------------------------------------------

/**
 * Parse a role menu that may arrive as a JSON string (REST), an object
 * (FlatBuffer-unpacked), or be absent/corrupt. Returns null when
 * unparseable so callers can fall back to the catalogue menu instead of
 * crashing. Shared by the wizard's Users & Roles step and any other
 * role-menu consumer.
 */
export function safeParseRoleMenu(raw: unknown): RoleMenuT | null {
  try {
    const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!obj || typeof obj !== "object" || !Array.isArray((obj as RoleMenuJson).pages)) {
      return null;
    }
    return menuJsonToRoleMenuT(obj as RoleMenuJson);
  } catch {
    return null;
  }
}
